import { FacebookFilled, InstagramFilled } from '@ant-design/icons';
import { createFileRoute } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import { useEffect } from 'react';
import CommunityTickets from '~/components/tickets/CommunityTickets';
import EarlyBirdTicket from '~/components/tickets/EarlyBirdTickets';
import { ticketData as data } from '~/data/tickets';
import { PageSection } from '~/routes/__root';
import { langStore } from '~/store/langStore';
import { cn } from '~/utils';
import { usePosthogEvent } from '~/utils/posthog';

interface TicketSectionProps {
  title: string;
  children: React.ReactNode;
  isFirst?: boolean;
  className?: string;
}

function TicketSection({ title, children, isFirst, className }: TicketSectionProps) {
  return (
    <>
      <div className="mt-8 flex justify-between">
        <h1 className="text-left text-2xl font-bold">{title}</h1>
        {isFirst && <img src="csillag2.png" className="size-20" />}
      </div>
      <div className={cn('mx-auto flex flex-col items-center justify-center py-10 md:flex-row', className)}>
        {children}
      </div>
    </>
  );
}

export const Route = createFileRoute('/tickets')({
  component: RouteComponent,
});

function RouteComponent() {
  const locale = useStore(langStore, s => s);
  const track = usePosthogEvent();
  useEffect(() => {
    track('tickets_page_view', { locale });
  }, [locale, track]);
  const texts = {
    HU: { huf: 'Online jegyek forintban:', dinar: 'Online jegyek dinárban:', community: 'Offline jegyek:' },
    EN: { huf: 'Online tickets in forints:', dinar: 'Online tickets in dinars:', community: 'Offline tickets:' },
    RS: { huf: 'Onlajn karte u forintama:', dinar: 'Onlajn karte u dinarima:', community: 'Oflajn karte:' },
  }[locale];

  const locationsArray = data.communityLocations.reduce((acc, curr) => {
    const existingLocation = acc.find(loc => loc.name === curr.HU);
    if (existingLocation) {
      existingLocation.places.push(curr);
    }
    else {
      acc.push({
        name: curr.HU,
        image: `/tickets/${curr.HU.toLowerCase()}.png`,
        places: [curr],
      });
    }
    return acc;
  }, [] as Array<{ name: string; image: string; places: typeof data.communityLocations }>);

  const hufSection = (
    <TicketSection title={texts.huf} isFirst={locale !== 'RS'}>
      <EarlyBirdTicket
        position={data.position[locale]}
        link={data.links.tixaLink}
        priceHuf="20 000"
        text={data.buy[locale]}
        pricepos="56.148"
        onClick={() => track('ticket_purchase_click', { type: 'early_bird', currency: 'HUF', price: '20000', locale })}
      />
      <div>
        <CommunityTickets
          position={data.position[locale]}
          link={data.links.tixaLink}
          priceHuf="40 000"
          text={data.buy[locale]}
          pricepos="65.191"
          onClick={() => track('ticket_purchase_click', { type: 'community', currency: 'HUF', price: '40000', locale })}
        />
      </div>
    </TicketSection>
  );

  const dinarSection = (
    <TicketSection title={texts.dinar} isFirst={locale === 'RS'}>
      <EarlyBirdTicket
        position={data.position[locale]}
        link={data.links.gigstixLink}
        priceRsd="5 000"
        text={data.buy[locale]}
        pricepos="56.148"
        yPos={146.227}
        onClick={() => track('ticket_purchase_click', { type: 'early_bird', currency: 'RSD', price: '5000', locale })}
      />
      <CommunityTickets
        position={data.position[locale]}
        link={data.links.gigstixLink}
        priceRsd="12 000"
        text={data.buy[locale]}
        pricepos="65.191"
        yPos={146.227}
        onClick={() => track('ticket_purchase_click', { type: 'community', currency: 'RSD', price: '12000', locale })}
      />
    </TicketSection>
  );

  return (
    <PageSection width="narrow">
      {locale === 'RS'
        ? (
            <>
              {dinarSection}
              <hr className="h-4 text-orange" />
              {hufSection}
            </>
          )
        : (
            <>
              {hufSection}
              <hr className="h-4 text-orange" />
              {dinarSection}
            </>
          )}
      <hr className="h-4 text-orange" />
      <TicketSection title={texts.community} className="flex flex-wrap gap-4">
        {locationsArray.map(location => (
          <div key={location.name}>
            <img src={location.image} alt={location.name} />
            <div className="flex flex-col gap-2">
              {location.places.map(place => (
                <div key={place.name} className="flex justify-between">
                  <h2>{place.name}</h2>
                  <div className="flex gap-2">
                    {place.fb && (
                      <a
                        href={place.fb}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit ${place.name}'s Facebook page`}
                      >
                        <FacebookFilled />
                      </a>
                    )}
                    {place.ig && (
                      <a
                        href={place.ig}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit ${place.name}'s Instagram page`}
                      >
                        <InstagramFilled />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </TicketSection>
    </PageSection>
  );
}
